import { GridColDef } from '@mui/x-data-grid-premium'
import { useTranslation } from 'react-i18next'
import { useColumnConfig } from '@omnea-app/fe-tables/columns/useColumnConfig'
import { applyDefaultColumnDefinitionsToColumns } from '@omnea-app/fe-tables/utils/columnDefaults'
import { useRfxStatusLabel } from './useRfxStatusLabel'

interface RfxColumnsConfigs {
  sortable?: boolean
}

type SourcingEventRowBase = {
  id: string // Required by DataGrid, corresponds to the rfx or auction id
  requestId: string
  requestName: string
  createdAt: string
  dueDate: string | null
}

export type SourcingEventRow =
  | (SourcingEventRowBase & {
      kind: 'RFX'
      status: 'SETUP' | 'COLLECT_RESPONSES' | 'RESPONSES_CLOSED' | 'SCORE_RESPONSES' | 'PICK_SUPPLIER' | 'COMPLETE'
      round: number
      deadlineType: 'SUBMISSIONS' | 'SCORING' | 'AWARD' | null
    })
  | (SourcingEventRowBase & { kind: 'AUCTION'; status: null; deadlineType: 'BIDDING_CLOSES' })

export const useRfxColumns = ({ sortable = true }: RfxColumnsConfigs): GridColDef<SourcingEventRow>[] => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'rfx-section' })
  const rfxStatusLabel = useRfxStatusLabel()

  const defaultColumns = useColumnConfig<SourcingEventRow>({
    columnConfig: [
      {
        field: 'requestName',
        headerName: t('table.name'),
        hideable: false,
        sortable,
        columnType: 'text',
      },
      {
        field: 'status',
        columnType: 'chip-single',
        sortable,
        headerName: t('table.status'),
        valueGetter: (_, row) => rfxStatusLabel(row),
        getColor: (row) => (row.kind === 'AUCTION' ? 'info' : 'neutral'),
      },
      {
        field: 'deadlineType',
        columnType: 'text',
        sortable,
        headerName: t('table.deadlineType'),
        valueGetter: (_, row) => (row.deadlineType ? t(`table.deadline-types.${row.deadlineType}`) : null),
      },
      {
        columnType: 'date',
        field: 'dueDate',
        sortable,
        headerName: t('table.due'),
      },
    ],
  })

  return applyDefaultColumnDefinitionsToColumns(defaultColumns)
}
